import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import Notification from '../components/Notification';
import '../styles/verify-password.css';

const VerifyPassword = () => {
    const { userId } = useParams();
    const [code, setCode] = useState(new Array(6).fill(''));
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: 'success', header: '' });
    const [canResend, setCanResend] = useState(false);
    const [counter, setCounter] = useState(60);
    const [resendAttempts, setResendAttempts] = useState(0);
    const inputRefs = useRef([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (counter > 0) {
            const timer = setTimeout(() => setCounter(counter - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [counter]);

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;
        const newCode = [...code];
        newCode[index] = element.value;
        setCode(newCode);

        if (element.value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const verificationCode = code.join('');
        setLoading(true);
        try {
            const response = await fetch(`${config.backendUrl}/users/verify-reset-password-code/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: verificationCode, user_id: userId }),
            });

            if (response.ok) {
                setNotification({ message: 'Code vérifié avec succès ! Redirection...', type: 'success', header: 'Succès' });
                setTimeout(() => navigate(`/set-password/${userId}`), 3000);
            } else {
                const data = await response.json();
                setNotification({ message: data.message || 'Échec de la vérification. Le code est peut-être invalide ou expiré.', type: 'error', header: 'Erreur' });
            }
        } catch (error) {
            setNotification({ message: 'Une erreur est survenue. Veuillez réessayer.', type: 'error', header: 'Erreur' });
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${config.backendUrl}/users/send-verification-code/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId }),
            });

            if (response.ok) {
                setNotification({ message: 'Code de vérification renvoyé avec succès !', type: 'success', header: 'Succès' });
                setCanResend(false);
                setCounter(180);
                setResendAttempts(prev => prev + 1); // Increment resend attempts

                // Clear the code state and reset the input fields
                setCode(new Array(6).fill(''));
                inputRefs.current.forEach((input) => {
                    if (input) {
                        input.value = ''; // Clear the input field
                    }
                });

                // Focus on the first input field
                inputRefs.current[0]?.focus();
            } else {
                const data = await response.json();
                setNotification({ message: data.message || 'Échec de l\'envoi du code.', type: 'error', header: 'Erreur' });
                setResendAttempts(prev => prev + 1); // Increment attempts even on failure
            }
        } catch (error) {
            setNotification({ message: 'Une erreur est survenue. Veuillez réessayer.', type: 'error', header: 'Erreur' });
            setResendAttempts(prev => prev + 1); // Increment attempts on error
        } finally {
            setLoading(false);
        }
    };

    const isCodeComplete = code.every((digit) => digit !== '');

    return (
        <div className="verify-container">
            {loading && <LoadingSpinner />}
            <Notification
                message={notification.message}
                type={notification.type}
                header={notification.header}
                onClose={() => setNotification({ message: '', type: 'success', header: '' })}
            />
            <div className="verify-card">
                <h2 className="verify-title">Vérifiez votre code</h2>
                <p className="verify-instructions">
                    Un code de vérification a été envoyé à votre numéro de téléphone. Entrez le code ci-dessous pour vérifier votre identité.
                </p>
                <form className="verify-form" onSubmit={handleSubmit}>
                    <div className="verify-inputs">
                        {code.map((digit, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleChange(e.target, index)}
                                onFocus={(e) => e.target.select()}
                                ref={(el) => (inputRefs.current[index] = el)}
                                className="verify-input"
                            />
                        ))}
                    </div>
                    <button type="submit" className="verify-submit" disabled={!isCodeComplete}>
                        Vérifier
                    </button>
                </form>
                <div className='resend-container'>
                    <p className="resend-text">Vous n'avez pas reçu le code ?</p>
                    <button
                        onClick={handleResendCode}
                        disabled={!canResend}
                        className={`resend-button ${!canResend ? 'disabled' : ''}`}
                    >
                        Renvoyer le code {canResend ? '' : `(${counter}s)`}
                    </button>
                </div>
                
                {/* Only show WhatsApp contact after 2 or more resend attempts */}
                {resendAttempts >= 2 && (
                    <div className="whatsapp-contact-container">
                        <p className="whatsapp-text">
                            Toujours pas de code?
                            <a 
                                href="https://wa.me/243851054526?text=Bonjour,%20je%20n'arrive%20pas%20à%20recevoir%20le%20code%20de%20vérification%20SMS%20sur%20Wennze.%20Pouvez-vous%20m'aider%20?"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-link"
                            >
                                Contactez-nous
                            </a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyPassword;